import { z } from 'zod';
import { CardType, PaymentRail } from '../../../payment.types';
import { StoredMaskedPanSchema } from '../../masked_pan.schemas';
import {
    type CloverEcommerceBrowserTokenizationSession,
    CloverBrowserTokenizationSessionType,
    type CloverCreditCardTokenizedCardMetadata,
    type CloverDebitCardTokenizedCardMetadata,
    type CloverEcommerceBrowserTokenizationResult,
    type CloverEcommerceTokenizationSuccessResponse,
    type CloverEcommerceTokenizedCard,
    type CloverStoredCardMetadataTokenizationResult,
    type CloverTokenizationResult,
    CloverTokenizationResultType,
    type CloverTokenizedCardMetadata,
} from '../../../types/ipg/clover/clover-tokenization.types';

export const CloverTokenizationTokenSchema: z.ZodString = z.string().regex(/^clv_[A-Za-z0-9]+$/);

export const CloverEcommerceBrowserTokenizationSessionSchema:
    z.ZodObject<{
        browser_tokenization_session_type: z.ZodLiteral<
            typeof CloverBrowserTokenizationSessionType
        >;
        ecommerce_api_key: z.ZodString;
        ecommerce_tokenization_url: z.ZodString;
        terminal_tokenization_supported: z.ZodLiteral<true>;
    }> =
    z
        .object({
            browser_tokenization_session_type: z.literal(
                CloverBrowserTokenizationSessionType
            ),
            ecommerce_api_key: z.string().min(1),
            ecommerce_tokenization_url: z.string().url(),
            terminal_tokenization_supported: z.literal(true),
        })
        .strict() satisfies z.ZodType<CloverEcommerceBrowserTokenizationSession>;

export const CloverEcommerceTokenizedCardSchema: z.ZodObject<{
    id: z.ZodOptional<z.ZodString>;
    token: z.ZodOptional<z.ZodString>;
    first6: z.ZodOptional<z.ZodString>;
    last4: z.ZodOptional<z.ZodString>;
    exp_month: z.ZodOptional<z.ZodString>;
    exp_year: z.ZodOptional<z.ZodString>;
    brand: z.ZodOptional<z.ZodString>;
    name: z.ZodOptional<z.ZodString>;
    country: z.ZodOptional<z.ZodString>;
}> =
    z
        .object({
            id: z.string().min(1).optional(),
            token: z.string().min(1).optional(),
            first6: z.string().regex(/^\d{6}$/).optional(),
            last4: z.string().regex(/^\d{4}$/).optional(),
            exp_month: z.string().regex(/^\d{2}$/).optional(),
            exp_year: z.string().regex(/^(\d{2}|\d{4})$/).optional(),
            brand: z.string().min(1).optional(),
            name: z.string().min(1).optional(),
            country: z.string().min(1).optional(),
        })
        .strip() satisfies z.ZodType<CloverEcommerceTokenizedCard>;

export interface CloverEcommerceTokenizationProviderResponse {
    readonly id?: string;
    readonly token?: string;
    readonly card?: CloverEcommerceTokenizedCard;
    readonly created?: number;
}

export const CloverEcommerceTokenizationProviderResponseSchema: z.ZodType<CloverEcommerceTokenizationProviderResponse> =
    z
        .object({
            id: CloverTokenizationTokenSchema.optional(),
            token: CloverTokenizationTokenSchema.optional(),
            card: CloverEcommerceTokenizedCardSchema.optional(),
            created: z.number().int().min(0).optional(),
        })
        .strip()
        .superRefine((value, context) => {
            if (value.id === undefined && value.token === undefined) {
                context.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Clover tokenization response is missing a source identifier.',
                    path: ['token'],
                });
            }
            if (value.id !== undefined && value.token !== undefined && value.id !== value.token) {
                context.addIssue({
                    code: z.ZodIssueCode.custom,
                    message: 'Clover tokenization response has conflicting source identifiers.',
                    path: ['token'],
                });
            }
        }) satisfies z.ZodType<CloverEcommerceTokenizationProviderResponse>;

export const CloverEcommerceTokenizationSuccessResponseSchema: z.ZodType<CloverEcommerceTokenizationSuccessResponse> =
    CloverEcommerceTokenizationProviderResponseSchema.transform((value, context) => {
        const token: string | undefined = value.token ?? value.id;
        if (token === undefined) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Clover tokenization response is missing a source identifier.',
                path: ['token'],
            });
            return z.NEVER;
        }
        return {
            ...value,
            token,
        };
    });

export const CloverCreditCardTokenizedCardMetadataSchema: z.ZodObject<{
    payment_rail: z.ZodLiteral<typeof PaymentRail.CREDIT_CARD>;
    masked_pan: typeof StoredMaskedPanSchema;
    card_type: z.ZodLiteral<typeof CardType.CREDIT>;
    card_brand: z.ZodString;
    cardholder_name: z.ZodString;
    expiry_date: z.ZodOptional<z.ZodString>;
    first6: z.ZodOptional<z.ZodString>;
    last4: z.ZodOptional<z.ZodString>;
}> =
    z
        .object({
            payment_rail: z.literal(PaymentRail.CREDIT_CARD),
            masked_pan: StoredMaskedPanSchema,
            card_type: z.literal(CardType.CREDIT),
            card_brand: z.string().min(1),
            cardholder_name: z.string().min(1),
            expiry_date: z
                .string()
                .regex(/^\d{2}\/\d{2}$/)
                .optional(),
            first6: z.string().regex(/^\d{6}$/).optional(),
            last4: z.string().regex(/^\d{4}$/).optional(),
        })
        .strict() satisfies z.ZodType<CloverCreditCardTokenizedCardMetadata>;

export const CloverDebitCardTokenizedCardMetadataSchema: z.ZodObject<{
    payment_rail: z.ZodLiteral<typeof PaymentRail.DEBIT_CARD>;
    masked_pan: typeof StoredMaskedPanSchema;
    card_type: z.ZodLiteral<typeof CardType.DEBIT>;
    card_brand: z.ZodString;
    cardholder_name: z.ZodString;
    expiry_date: z.ZodOptional<z.ZodString>;
    first6: z.ZodOptional<z.ZodString>;
    last4: z.ZodOptional<z.ZodString>;
}> =
    z
        .object({
            payment_rail: z.literal(PaymentRail.DEBIT_CARD),
            masked_pan: StoredMaskedPanSchema,
            card_type: z.literal(CardType.DEBIT),
            card_brand: z.string().min(1),
            cardholder_name: z.string().min(1),
            expiry_date: z
                .string()
                .regex(/^\d{2}\/\d{2}$/)
                .optional(),
            first6: z.string().regex(/^\d{6}$/).optional(),
            last4: z.string().regex(/^\d{4}$/).optional(),
        })
        .strict() satisfies z.ZodType<CloverDebitCardTokenizedCardMetadata>;

export const CloverTokenizedCardMetadataSchema: z.ZodType<CloverTokenizedCardMetadata> =
    z.discriminatedUnion('payment_rail', [
        CloverCreditCardTokenizedCardMetadataSchema,
        CloverDebitCardTokenizedCardMetadataSchema,
    ]);

export const CloverEcommerceBrowserTokenizationResultSchema: z.ZodObject<{
    result_type: z.ZodLiteral<
        typeof CloverTokenizationResultType.ECOMMERCE_BROWSER_TOKENIZATION
    >;
    token_response: typeof CloverEcommerceTokenizationSuccessResponseSchema;
    card_metadata: z.ZodOptional<typeof CloverTokenizedCardMetadataSchema>;
}> =
    z
        .object({
            result_type: z.literal(
                CloverTokenizationResultType.ECOMMERCE_BROWSER_TOKENIZATION
            ),
            token_response: CloverEcommerceTokenizationSuccessResponseSchema,
            card_metadata: CloverTokenizedCardMetadataSchema.optional(),
        })
        .strict() satisfies z.ZodType<CloverEcommerceBrowserTokenizationResult>;

export const CloverStoredCardMetadataTokenizationResultSchema: z.ZodObject<{
    result_type: z.ZodLiteral<
        typeof CloverTokenizationResultType.STORED_CARD_METADATA
    >;
    token: typeof CloverTokenizationTokenSchema;
    card_metadata: typeof CloverTokenizedCardMetadataSchema;
}> =
    z
        .object({
            result_type: z.literal(
                CloverTokenizationResultType.STORED_CARD_METADATA
            ),
            token: CloverTokenizationTokenSchema,
            card_metadata: CloverTokenizedCardMetadataSchema,
        })
        .strict() satisfies z.ZodType<CloverStoredCardMetadataTokenizationResult>;

export const CloverTokenizationResultSchema: z.ZodType<CloverTokenizationResult> =
    z.discriminatedUnion('result_type', [
        CloverEcommerceBrowserTokenizationResultSchema,
        CloverStoredCardMetadataTokenizationResultSchema,
    ]);
